"use client";
import Banner from "@/app/[locale]/(user-page)/report/components/Banner";
import ReportsPublished from "@/app/[locale]/(user-page)/report/components/ReportsPublished";
import UpcomingReports from "@/app/[locale]/(user-page)/report/components/UpcomingReports";
import useDeviceDetect from "@/hooks/useMobile";
import "@/styles/Report/style.css";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Menu, MenuItem } from "@mui/material";
import Image from "next/image";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import "swiper/css";
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReportFeatured from "@/app/[locale]/(user-page)/report/components/ReportsFeature";
import TrialConsumer from "@/app/[locale]/(user-page)/report/components/Trial";
import { useUserQuery } from "@/hooks/Query/useUserQuery";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import clsx from "clsx";
import "moment/locale/en-gb";
import "moment/locale/id";
import "moment/locale/vi";
import { useTranslations } from "next-intl";

interface TabPanelProps {
  categories?: any;
  isShowFavorite?: boolean;
  isMobile?: boolean;
  locale: string;
}
export default function Page({
  categories,
  isShowFavorite,
  isMobile,
  locale,
}: TabPanelProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { data: user } = useUserQuery({
    options: {
      enabled: false,
    },
  });
  const isLoggedIn = !!user;
  const t = useTranslations("messages");
  const [value, setValue] = useState<{
    type: string;
    id?: string | number;
  }>({
    type: "all",
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);
  const [showLeft, setshowLeft] = useState<boolean>(false);
  const [showRight, setshowRight] = useState<boolean>(false);
  const isMobile2 = useDeviceDetect();
  const handleChange = (type: string, id?: string | number) => {
    setValue({
      type,
      id,
    });
  };

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -300,
        behavior: "smooth",
      });
    }
  };
  const handleScrollCheck = (e: any) => {
    const target = e.target as HTMLDivElement;
    const scrollLeft = target.scrollLeft;
    const scrollWidth = target.scrollWidth;
    const clientWidth = target.clientWidth;
    setshowLeft(scrollLeft > 0);
    setshowRight(scrollLeft + clientWidth <= scrollWidth - 1);
  };
  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: 300,
        behavior: "smooth",
      });
    }
  };
  useEffect(() => {
    const handleShowScrollNext = () => {
      if (scrollRef.current && categoryRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        const {
          scrollLeft: categoryScrollLeft,
          scrollWidth: categoryScrollWidth,
          clientWidth: categoryClientWidth,
        } = categoryRef.current;
        setshowLeft(scrollLeft > 0 || categoryScrollLeft > 0);
        setshowRight(
          scrollLeft + clientWidth <= scrollWidth - 1 ||
            categoryScrollLeft + categoryClientWidth <= categoryScrollWidth - 1
        );
      }
    };
    if (scrollRef.current) {
      scrollRef.current.addEventListener("scroll", handleShowScrollNext);
      handleShowScrollNext();
    }
    return () => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener("scroll", handleShowScrollNext);
      }
    };
  }, [isLoggedIn]);

  /**===========MOBILE */
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleMenuItemClick = (
    type: string,
    id: string | number | null = null
  ) => {
    handleChange(type, id === null ? undefined : id);
    handleClose();
  };

  // Get current selected item display
  const getCurrentDisplay = () => {
    if (value.type === "all") {
      return t("home_all_reports");
    }
    if (value.type === "favorite") {
      return (
        <Box
          translation-key="home_favorite"
          sx={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <FavoriteBorderIcon sx={{ width: 20, height: 20 }} />
          {t("home_favorite")}
        </Box>
      );
    }
    if (value.type === "category") {
      const category = categories?.find((item: any) => item.id === value.id);
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {category?.icon && (
            <Image
              priority
              src={category.icon}
              alt={category.icon}
              width={20}
              height={20}
              className={clsx("h-5 imgIcon")}
              style={{
                filter:
                  "invert(100%) sepia(100%) saturate(8%) hue-rotate(189deg) brightness(103%) contrast(103%)",
              }}
            />
          )}
          {category?.name}
        </Box>
      );
    }
    return t("home_all_reports");
  };

  useEffect(() => {
    if (!open) return;
    if (open) {
      const handleScroll = (event: any) => {
        if (menuRef.current && menuRef.current.contains(event.target)) {
          const hasVerticalScrollbar =
            menuRef.current.scrollHeight > menuRef.current.clientHeight;
          const hasHorizontalScrollbar =
            menuRef.current.scrollWidth > menuRef.current.clientWidth;
          // Nếu phần tử đang cuộn có overflow (có thể scroll được), thì không đóng menu
          if (hasVerticalScrollbar || hasHorizontalScrollbar) {
            return;
          }
        }
        handleClose();
      };
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("touchmove", handleScroll, true);
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("touchmove", handleScroll, true);
      };
    }
  }, [open, handleClose]);

  return (
    <Fragment>
      <Banner userServer={user} isLoggedIn={isLoggedIn} isMobile={isMobile} />
      <Box className={`${(user || isLoggedIn) && "pt-1"}`} id="report-content">
        <Box className="flex justify-center pb-12">
          <Box
            className="px-0 md:px-10 xl:px-0 xl:max-w-[1240px] w-full mt-5 md:mt-10 mb-12 scroll-mt-28"
            id="boxContainer"
          >
            {user && <TrialConsumer user={user} />}
            <Box className="overflow-hidden gap-1 md:gap-8 grid">
              <Box
                className="hidden sm:flex flex-wrap gap-4 py-6"
                sx={{
                  alignItems: "center",
                  position: "relative",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
                id="category-scroll-container"
                ref={categoryRef}
              >
                {/* Left scroll button */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "white",
                    borderRadius: "50%",
                    boxShadow: 1,
                    cursor: showLeft ? "pointer" : "default",
                    opacity: showLeft ? "1" : "0",
                    transition: "opacity 0.3s ease, visibility 0.3s ease",
                  }}
                  onClick={handleScrollLeft}
                >
                  <KeyboardArrowLeftIcon />
                </Box>
                {/* Scrollable tabs */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    overflowX: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                    width: "100%",
                  }}
                  ref={scrollRef}
                  onScroll={handleScrollCheck}
                >
                  <Box
                    onClick={() => handleChange("all")}
                    sx={{
                      cursor: "pointer",
                      bgcolor:
                        value.type === "all"
                          ? "var(--cimigo-blue-dark-1)"
                          : "var(--ciBlue-1)",
                      color:
                        value.type === "all" ? "white" : "var(--ciBlue-900)",
                      padding: "8px 24px",
                      borderRadius: "50px",
                      fontWeight: 500,
                      fontSize: "16px",
                      transition: "background 0.2s",
                      "&:hover": {
                        bgcolor:
                          value.type === "all"
                            ? "var(--cimigo-blue)"
                            : "var(--ciBlue-2)",
                      },
                    }}
                  >
                    {t("home_all_reports")}
                  </Box>

                  {isLoggedIn && isShowFavorite && (
                    <Box
                      onClick={() => handleChange("favorite")}
                      sx={{
                        cursor: "pointer",
                        bgcolor:
                          value.type === "favorite"
                            ? "var(--cimigo-blue-dark-1)"
                            : "var(--ciBlue-1)",
                        color:
                          value.type === "favorite"
                            ? "white"
                            : "var(--ciBlue-900)",
                        padding: "8px 24px",
                        borderRadius: "50px",
                        fontWeight: 500,
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        transition: "background 0.2s",
                        "&:hover": {
                          bgcolor:
                            value.type === "favorite"
                              ? "var(--cimigo-blue)"
                              : "var(--ciBlue-2)",
                        },
                      }}
                    >
                      <FavoriteBorderIcon sx={{ width: 24, height: 24 }} />
                      {t("home_favorite")}
                    </Box>
                  )}
                  {categories?.length > 0 &&
                    categories.map((item: any) => (
                      <Box
                        key={item?.id}
                        onClick={() => handleChange("category", item?.id)}
                        sx={{
                          cursor: "pointer",
                          bgcolor:
                            value.id === item?.id
                              ? "var(--cimigo-blue-dark-1)"
                              : "var(--ciBlue-1)",
                          color:
                            value.id === item?.id
                              ? "white"
                              : "var(--ciBlue-900)",
                          padding: "8px 24px",
                          borderRadius: "50px",
                          fontWeight: 500,
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          transition: "background 0.2s",
                          "&:hover": {
                            bgcolor:
                              value.id === item?.id
                                ? "var(--cimigo-blue)"
                                : "var(--ciBlue-2)",
                          },
                        }}
                      >
                        {item?.icon && (
                          <Image
                            priority
                            src={item?.icon}
                            alt={item?.icon}
                            width={24}
                            height={24}
                            className={clsx("h-6 imgIcon")}
                            style={{
                              filter:
                                value.id === item?.id
                                  ? "invert(100%) sepia(100%) saturate(8%) hue-rotate(189deg) brightness(103%) contrast(103%)"
                                  : "invert(29%) sepia(85%) saturate(863%) hue-rotate(180deg) brightness(95%) contrast(90%)",
                            }}
                          />
                        )}
                        {item?.name}
                      </Box>
                    ))}
                </Box>
                {/* Right scroll button */}
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "white",
                    borderRadius: "50%",
                    boxShadow: 1,
                    cursor: showRight ? "pointer" : "default",
                    opacity: showRight ? "1" : "0",
                    transition: "opacity 0.3s ease, visibility 0.3s ease",
                  }}
                  onClick={handleScrollRight}
                >
                  <KeyboardArrowRightIcon />
                </Box>
              </Box>
              <Box
                sx={{
                  display: { xs: "flex", sm: "none" },
                  justifyContent: "center",
                  py: 3,
                  px: 2,
                  width: "100%",
                }}
              >
                <Box
                  onClick={handleClick}
                  sx={{
                    cursor: "pointer",
                    bgcolor: "var(--cimigo-blue-dark-1)",
                    color: "white",
                    padding: "12px 24px",
                    borderRadius: "50px",
                    fontWeight: 500,
                    fontSize: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    minWidth: "200px",
                    justifyContent: "space-between",
                    transition: "background 0.2s",
                    width: "100%", // Make the button full width
                    "&:hover": {
                      bgcolor: "var(--cimigo-blue)",
                    },
                  }}
                >
                  {getCurrentDisplay()}
                  <KeyboardArrowDownIcon
                    sx={{
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </Box>

                {/* Dropdown category menu */}
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  sx={{
                    "& .MuiMenu-list": {
                      "& li": {
                        fontSize: "16px !important",
                        fontWeight: 500,
                        lineHeight: "24px !important",
                        fontFamily: "Montserrat, sans-serif !important",
                      },
                    },
                  }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  PaperProps={{
                    ref: menuRef,
                    sx: {
                      borderRadius: "12px",
                      minWidth: "200px",
                      width: "100%", // Make the menu full width
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      maxHeight: "304px",
                      overflowY: "auto",
                    },
                  }}
                  MenuListProps={{
                    sx: {
                      width: "100%",
                    },
                  }}
                >
                  {/*Home all reports*/}
                  <MenuItem
                    onClick={() => handleMenuItemClick("all")}
                    selected={value.type === "all"}
                    sx={{
                      py: 1.5,
                      px: 3,
                      bgcolor:
                        value.type === "all"
                          ? "var(--ciBlue-1)"
                          : "transparent",
                      "&:hover": {
                        bgcolor: "var(--ciBlue-2)",
                      },
                      width: "100%",
                      gap: "12px",
                      "&.Mui-selected": {
                        bgcolor: "var(--ciBlue-1)",
                        "&:hover": {
                          bgcolor: "var(--ciBlue-1)", // Giữ nguyên màu khi đã được chọn và hover
                        },
                      },
                    }}
                  >
                    {/* <Box sx={{
                        width: 20, height: 20,
                        color: "var(--ciBlue-900)",
                      }} /> */}
                    {t("home_all_reports")}
                  </MenuItem>

                  {/** Favorite reports */}
                  {isLoggedIn && isShowFavorite && (
                    <MenuItem
                      onClick={() => handleMenuItemClick("favorite")}
                      sx={{
                        py: 1.5,
                        px: 3,
                        bgcolor:
                          value.type === "favorite"
                            ? "var(--ciBlue-1)"
                            : "transparent",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        "&:hover": {
                          bgcolor: "var(--ciBlue-2)",
                        },
                        width: "100%",
                      }}
                    >
                      <FavoriteBorderIcon
                        sx={{
                          width: 20,
                          height: 20,
                          color: "var(--ciBlue-900)",
                        }}
                      />
                      {t("home_favorite")}
                    </MenuItem>
                  )}

                  {/* Category items */}
                  {categories?.length > 0 &&
                    categories.map((item: any) => (
                      <MenuItem
                        key={item.id}
                        onClick={() => handleMenuItemClick("category", item.id)}
                        sx={{
                          py: 1.5,
                          px: 3,
                          bgcolor:
                            value.id === item.id
                              ? "var(--ciBlue-1)"
                              : "transparent",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          "&:hover": {
                            bgcolor: "var(--ciBlue-2)",
                          },
                          width: "100%",
                        }}
                      >
                        {item.icon && (
                          <Image
                            priority
                            src={item.icon}
                            alt={item.icon}
                            width={20}
                            height={20}
                            className={clsx("h-5 imgIcon")}
                            style={{
                              filter:
                                value.id === item.id
                                  ? "invert(29%) sepia(85%) saturate(863%) hue-rotate(180deg) brightness(95%) contrast(90%)"
                                  : "invert(29%) sepia(85%) saturate(863%) hue-rotate(180deg) brightness(95%) contrast(90%)",
                            }}
                          />
                        )}
                        {item.name}
                      </MenuItem>
                    ))}
                </Menu>
              </Box>
              {/* NEW SECTION */}
              <Box
                className="gap-8 grid "
                sx={{
                  paddingX: {
                    xs: "20px",
                    md: "unset",
                  },
                }}
              >
                {/* note :dat */}
                <UpcomingReports
                  locale={locale}
                  isMobile={isMobile || isMobile2}
                  type={value.type}
                  isLoggedIn={isLoggedIn}
                  categoryId={value.id}
                  userId={user?.id}
                  queryKey={`report-upcoming`}
                />
                <Box className="gap-12 grid">
                  {value.type === "favorite" ? null : (
                    <ReportFeatured
                      locale={locale}
                      isMobile={isMobile || isMobile2}
                      type={value.type}
                      categoryId={value.id}
                      isLoggedIn={isLoggedIn}
                      queryKey={`report-featured`}
                    />
                  )}
                  <ReportsPublished
                    locale={locale}
                    isMobile={isMobile || isMobile2}
                    type={value.type}
                    isLoggedIn={isLoggedIn}
                    categoryId={value.id}
                    userId={user?.id}
                    queryKey={`report-published`}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
}
